import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Developers from "../components/Developers";
import {
  ArrowPathRoundedSquareIcon,
  BanknotesIcon,
  BoltIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  GiftIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const benefits = [
  {
    title: "Cross-network delivery",
    description: "Send airtime across supported mobile networks from one Whistle Pulse account.",
    icon: ArrowPathRoundedSquareIcon,
  },
  {
    title: "Instant top-ups",
    description: "Credit customers, teams, or partners quickly when timing matters.",
    icon: BoltIcon,
  },
  {
    title: "Bulk distribution",
    description: "Run reward campaigns, employee benefits, and large-volume top-ups without manual work.",
    icon: UserGroupIcon,
  },
  {
    title: "Reliable reconciliation",
    description: "Track references, statuses, and transactions for clean finance and support workflows.",
    icon: ShieldCheckIcon,
  },
];

const useCases = [
  ["Customer rewards", "Give airtime incentives after purchases, referrals, surveys, or loyalty milestones."],
  ["Employee benefits", "Distribute airtime allowances to field teams, agents, and support staff."],
  ["Emergency credits", "Keep users connected when they need urgent communication support."],
  ["Promotional campaigns", "Reward campaign participation with instant airtime across networks."],
];

const transactions = [
  ["MTN", "233 50 123 4567", "GHS 10.00", "Delivered"],
  ["Telecel", "233 20 222 8181", "GHS 5.00", "Processing"],
  ["AirtelTigo", "233 27 450 7788", "GHS 15.00", "Delivered"],
];

function Airtime() {
  return (
    <div className="bg-slate-50">
      <Navbar />

      <section className="relative overflow-hidden bg-[#071d33] text-white">
        <div className="absolute inset-x-0 bottom-0 h-28 bg-slate-50" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[85rem] gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_0.95fr] lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">Airtime purchase</p>
            <h1 className="mt-5 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Airtime top-ups for rewards, teams, and customers.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50">
              Buy and distribute airtime across supported networks from a single platform. Whistle Pulse helps
              businesses automate incentives, staff allowances, emergency credits, and customer engagement campaigns.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://dashboard.whistlepulse.com/auth/register"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-400"
              >
                Start top-ups
              </a>
              <a
                href="/contact-us"
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Talk to sales
              </a>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-xl">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 shadow-2xl shadow-black/30 backdrop-blur">
              <div className="rounded-xl bg-[#041629] p-5">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div>
                    <p className="text-sm text-blue-200">Distribution wallet</p>
                    <p className="mt-1 text-3xl font-bold">GHS 24,860.00</p>
                  </div>
                  <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-sm font-semibold text-emerald-200">
                    active
                  </span>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {[
                    ["Today", "1,248"],
                    ["Success", "98.7%"],
                    ["Avg. time", "8 sec"],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-white/8 p-4">
                      <p className="text-xs text-blue-200">{label}</p>
                      <p className="mt-2 text-xl font-bold text-white">{value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 overflow-hidden rounded-xl border border-white/10">
                  {transactions.map(([network, number, amount, status]) => (
                    <div key={`${network}-${number}`} className="grid grid-cols-[1fr_auto] gap-3 border-b border-white/10 p-4 last:border-b-0">
                      <div>
                        <p className="font-semibold text-white">{network}</p>
                        <p className="mt-1 text-sm text-blue-200">{number}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-bold text-white">{amount}</p>
                        <p className={`mt-1 text-xs ${status === "Delivered" ? "text-emerald-200" : "text-amber-200"}`}>
                          {status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <benefit.icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h2 className="mt-5 text-lg font-bold text-slate-950">{benefit.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[85rem] gap-10 px-4 pb-16 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Common workflows</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">
            Turn airtime into a fast engagement channel.
          </h2>
          <p className="mt-5 text-base leading-7 text-slate-600">
            Airtime is practical, familiar, and instantly useful. Use it to support customers, reward behavior, and keep
            distributed teams connected.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {useCases.map(([title, description]) => (
            <div key={title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-blue-600" aria-hidden="true" />
                <div>
                  <h3 className="font-semibold text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[85rem] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-3 lg:px-8">
          {[
            [DevicePhoneMobileIcon, "Choose recipients", "Send one-off top-ups or upload a list for bulk distribution."],
            [BanknotesIcon, "Set value and network", "Select amount, currency, network details, and transaction reference."],
            [GiftIcon, "Deliver and track", "Monitor top-up status, reconcile references, and support customers quickly."],
          ].map(([Icon, title, description], index) => (
            <div key={title} className="relative rounded-lg border border-slate-200 p-6">
              <span className="absolute right-5 top-5 font-mono text-sm font-bold text-slate-200">0{index + 1}</span>
              <Icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h3 className="mt-5 text-xl font-bold text-slate-950">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 rounded-2xl bg-[#071d33] p-6 text-white sm:p-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
          <div>
            <Squares2X2Icon className="h-9 w-9 text-blue-300" aria-hidden="true" />
            <h2 className="mt-5 text-2xl font-bold">Built for business operations</h2>
          </div>
          <p className="text-sm leading-7 text-blue-50 sm:text-base">
            Use airtime distribution for marketing, customer care, field operations, agent networks, surveys, and
            loyalty programs, all from one reliable Whistle Pulse workflow.
          </p>
        </div>
      </section>

      <Developers />
      <Footer />
    </div>
  );
}

export default Airtime;
